"use client";

import { reasoningEffortForThinking } from "@perceptron/api/apiContracts/chatCompletions";
import { isCollectionAnnotation, type Annotation } from "@perceptron/core/types";

import { FeedbackButtons, type FeedbackContext } from "@demo/features/report";
import { useDetectStore } from "@demo/store/detect";
import { useModelStore } from "@demo/store/model";

/** Per-category detection counts ("car: 3") — the Slack message preview. */
const detectionsSummary = (annotations: Annotation[]): string =>
  annotations
    .filter(isCollectionAnnotation)
    .map((collection) => `${collection.label}: ${collection.children.length}`)
    .join("\n");

/**
 * Snapshot the detect run for a feedback report: the inputs that were sent
 * (image, the categories snapshotted at run time, config) plus the detections
 * the model returned.
 */
const detectFeedbackContext = (): FeedbackContext => {
  const { image, requestedCategories, outputType, enableThinking } = useDetectStore.getState();
  const annotations = useModelStore.getState().annotations;

  return {
    mode: "detect",
    requestBody: {
      image_url: image?.url ?? null,
      categories: requestedCategories,
      config: { output: outputType, reasoning_effort: reasoningEffortForThinking(enableThinking) },
      detections: annotations,
    },
    modelResponse: detectionsSummary(annotations),
    mediaUrls: image?.url ? [image.url] : [],
  };
};

/**
 * Detect's feedback affordance (dev-mode only, via the shared
 * {@link FeedbackButtons}): thumbs appear once a run completes and reset when
 * the next run starts or the surface is cleared.
 */
const DetectFeedback = () => {
  const status = useDetectStore((state) => state.status);
  const imageKey = useDetectStore((state) => state.image?.key);

  const resultKey = status === "done" && imageKey ? imageKey : null;

  return <FeedbackButtons getContext={detectFeedbackContext} resultKey={resultKey} />;
};

export default DetectFeedback;
